// Alerts / Recent Updates store — per company, persisted to localStorage.
// Shared by AlertsFeed (investor "Recent Updates") and ProductRoadmap
// (which auto-pushes a "milestone completed" alert when a milestone is
// marked Done).
//
// When Supabase is wired, swap the storage layer with an `alerts` table.

const STORAGE_PREFIX = 'finkloz_alerts_';
const CHANGE_EVENT = 'finkloz:alerts-changed';

function key(companyId) {
  return STORAGE_PREFIX + (companyId || 'default');
}

// Hydrate defaults lazily so they can change in investorData without
// breaking existing saved state.
function loadDefault() {
  try {
    // eslint-disable-next-line global-require
    const { ALERTS } = require('./investorData');
    if (Array.isArray(ALERTS) && ALERTS.length) return ALERTS.map(a => ({ ...a }));
  } catch { /* ignore */ }
  return [];
}

export function getAlerts(companyId) {
  try {
    const raw = localStorage.getItem(key(companyId));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch { /* ignore */ }
  return loadDefault();
}

export function saveAlerts(companyId, alerts) {
  try {
    localStorage.setItem(key(companyId), JSON.stringify(alerts));
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('saveAlerts failed', e);
  }
}

export function subscribeToAlertsChanges(callback) {
  const handler = () => callback();
  window.addEventListener(CHANGE_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}

// Convert a MonthSelector period key like "jan-2026" → "2026-01-15".
// Falls back to today's date if the key is unrecognized.
function selectedPeriodToDate(selectedPeriod) {
  const MONTHS = { jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06', jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12' };
  if (selectedPeriod) {
    const [mon, yr] = selectedPeriod.split('-');
    if (MONTHS[mon] && yr) return `${yr}-${MONTHS[mon]}-15`;
  }
  return new Date().toISOString().split('T')[0];
}

// Idempotent: pushes a milestone-completed alert keyed by milestoneId so
// toggling Done off/on doesn't create duplicates. If the milestone is
// un-Done later, call `removeMilestoneCompletionAlert` to take it down.
export function upsertMilestoneCompletionAlert(companyId, milestone, selectedPeriod) {
  if (!milestone || !milestone.id) return;
  const alertId = `ms-done-${milestone.id}`;
  const alerts = getAlerts(companyId);
  const existingIdx = alerts.findIndex(a => a.id === alertId);
  const entry = {
    id: alertId,
    type: 'milestone',
    date: selectedPeriodToDate(selectedPeriod),
    title: `Milestone completed: ${milestone.label}${milestone.period ? ` (${milestone.period})` : ''}`,
    _fromMilestone: true,
  };
  let next;
  if (existingIdx >= 0) {
    next = [...alerts];
    next[existingIdx] = { ...alerts[existingIdx], ...entry };
  } else {
    next = [entry, ...alerts];
  }
  saveAlerts(companyId, next);
}

export function removeMilestoneCompletionAlert(companyId, milestoneId) {
  if (!milestoneId) return;
  const alertId = `ms-done-${milestoneId}`;
  const alerts = getAlerts(companyId);
  const next = alerts.filter(a => a.id !== alertId);
  if (next.length !== alerts.length) saveAlerts(companyId, next);
}
